import * as React from 'react';
import {
  useCameraList,
  type BrandedCameraSummary as CameraSummary,
} from '@cguard/api-client/hooks/cameras';
import {
  useSetCameraAnalytics,
  type CameraAnalyticsCapability,
} from '@cguard/api-client/hooks/video-console';
import type { CameraId } from '@cguard/types';
import {
  PageContainer,
  PageHeader,
  Select,
  SelectContent,
  SelectItem,
  SelectTrigger,
  SelectValue,
} from '@cguard/design-system';
import { Check, CircleAlert, RotateCcw, Sparkles } from 'lucide-react';
import { useTranslation } from 'react-i18next';
import { AdminGuard } from '~/components/admin-guard';
import { useCameraStatusLabel } from '~/lib/camera-status';
import { withoutSeededDemoCameras } from '~/lib/production-roster';

/** Assignable edge tiers in menu order; wording is `admin.analytics.capability.<tier>`. */
export const CAPABILITIES: readonly CameraAnalyticsCapability[] = [
  'TierC',
  'TierMotion',
  'TierB',
  'TierLpr',
  'TierA',
  'TierAnomaly',
];

/** How long the "Saved" confirmation stays next to a camera. */
const SAVED_NOTICE_MS = 4_000;

interface CapabilitySave {
  readonly status: 'saving' | 'saved' | 'failed';
  readonly capability: CameraAnalyticsCapability;
}

export function AnalyticsAdminPage(): React.JSX.Element {
  return (
    <AdminGuard>
      <AnalyticsAdministration />
    </AdminGuard>
  );
}

function AnalyticsAdministration(): React.JSX.Element {
  const { t } = useTranslation('video');
  const statusLabel = useCameraStatusLabel();
  const capabilityItems = CAPABILITIES.map((tier) => ({
    value: tier,
    label: t('admin.analytics.capabilityOption', {
      label: t(`admin.analytics.capability.${tier}.label`),
      description: t(`admin.analytics.capability.${tier}.description`),
    }),
  }));
  const cameraQuery = useCameraList();
  const mutation = useSetCameraAnalytics();
  const [saves, setSaves] = React.useState<ReadonlyMap<CameraId, CapabilitySave>>(() => new Map());
  const cameras = withoutSeededDemoCameras(cameraQuery.data ?? []);
  const analyticsCount = cameras.filter((camera) => camera.tier !== 'TierC').length;

  React.useEffect(() => {
    const savedIds = [...saves].filter(([, save]) => save.status === 'saved').map(([id]) => id);
    if (savedIds.length === 0) return;
    const timer = window.setTimeout(() => {
      setSaves((current) => {
        const next = new Map(current);
        for (const id of savedIds) {
          if (next.get(id)?.status === 'saved') next.delete(id);
        }
        return next;
      });
    }, SAVED_NOTICE_MS);
    return () => window.clearTimeout(timer);
  }, [saves]);

  const assign = (camera: CameraSummary, capability: CameraAnalyticsCapability): void => {
    const record = (status: CapabilitySave['status']): void =>
      setSaves((current) => new Map(current).set(camera.id, { status, capability }));
    record('saving');
    mutation.mutateAsync({ cameraId: camera.id, capability }).then(
      () => record('saved'),
      () => record('failed'),
    );
  };

  return (
    <div className="admin-page">
      <PageContainer className="mx-auto max-w-[1180px]">
        <PageHeader
          breadcrumb={t('admin.restricted')}
          title={t('admin.analytics.title')}
          description={t('admin.analytics.description')}
          {...(!cameraQuery.isLoading && !cameraQuery.isError && cameras.length > 0
            ? {
                meta: t('admin.analytics.summary', {
                  count: analyticsCount,
                  total: cameras.length,
                }),
              }
            : {})}
        />

        <section className="analytics-table">
          <header className="analytics-table-header">
            <span>{t('admin.analytics.columns.camera')}</span>
            <span>{t('admin.analytics.columns.capability')}</span>
            <span>{t('admin.analytics.columns.status')}</span>
          </header>
          {cameraQuery.isLoading ? (
            <div className="analytics-empty">{t('admin.analytics.loading')}</div>
          ) : cameraQuery.isError ? (
            <div className="analytics-empty">
              <CircleAlert aria-hidden size={18} />
              {t('admin.analytics.error')}
            </div>
          ) : cameras.length === 0 ? (
            <div className="analytics-empty">{t('admin.analytics.empty')}</div>
          ) : (
            cameras.map((camera, index) => {
              const save = saves.get(camera.id);
              return (
                <div className="analytics-row" key={camera.id}>
                  <div className="analytics-camera">
                    <span className="camera-index">{String(index + 1).padStart(2, '0')}</span>
                    <span>
                      <strong>{camera.name}</strong>
                      <small className={`camera-video-status is-${camera.status}`}>
                        {statusLabel(camera.status)}
                      </small>
                    </span>
                  </div>
                  <Select
                    items={capabilityItems}
                    value={save?.status === 'saving' ? save.capability : camera.tier}
                    disabled={save?.status === 'saving'}
                    onValueChange={(next) => {
                      if (next) assign(camera, next as CameraAnalyticsCapability);
                    }}
                  >
                    <SelectTrigger
                      className="capability-select"
                      aria-label={t('admin.analytics.capabilityFor', { name: camera.name })}
                    >
                      <SelectValue />
                    </SelectTrigger>
                    <SelectContent>
                      {capabilityItems.map((item) => (
                        <SelectItem key={item.value} value={item.value}>
                          {item.label}
                        </SelectItem>
                      ))}
                    </SelectContent>
                  </Select>
                  <div className={`assignment-state ${save ? `is-${save.status}` : 'is-idle'}`}>
                    {save?.status === 'saving' ? (
                      <>
                        <RotateCcw className="animate-spin" aria-hidden size={13} />
                        {t('admin.analytics.saving')}
                      </>
                    ) : save?.status === 'saved' ? (
                      <>
                        <Check aria-hidden size={14} />
                        {t('admin.analytics.saved')}
                      </>
                    ) : save?.status === 'failed' ? (
                      t('admin.analytics.notSaved')
                    ) : camera.tier === 'TierC' ? (
                      t('admin.analytics.videoOnly')
                    ) : (
                      t('admin.analytics.analyticsOn')
                    )}
                  </div>
                  {save?.status === 'failed' ? (
                    <div className="analytics-row-error" role="alert">
                      <CircleAlert aria-hidden size={14} />
                      <span>{t('admin.analytics.saveFailed', { name: camera.name })}</span>
                      <button
                        type="button"
                        className="text-button"
                        onClick={() => assign(camera, save.capability)}
                      >
                        {t('common:actions.retry')}
                      </button>
                    </div>
                  ) : null}
                </div>
              );
            })
          )}
        </section>

        <section className="qualification-card">
          <span className="qualification-icon">
            <Sparkles aria-hidden size={19} />
          </span>
          <div>
            <p className="eyebrow">{t('admin.analytics.qualification.eyebrow')}</p>
            <h2>{t('admin.analytics.qualification.title')}</h2>
            <p>{t('admin.analytics.qualification.body')}</p>
          </div>
          <span className="pending-label">{t('admin.analytics.qualification.pending')}</span>
        </section>
      </PageContainer>
    </div>
  );
}
